import { Injectable } from '@angular/core';
import { InvoicesService } from '@app/entities/invoices/services/invoices.service';
import { PaginatedResults } from '@app/interfaces/paginated-results';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { InvoicesStateModel } from './invoices-state.model';
import { DownloadInvoice, LoadInvoices } from './invoices.actions';
import { Invoice } from '@app/entities/invoices/interfaces/invoice';
import { InvoiceFile } from '@app/entities/invoices/interfaces/invoice-file';

@State<InvoicesStateModel>({
  name: 'invoices',
})
@Injectable()
export class InvoicesState {
  constructor(private invoicesService: InvoicesService) {}

  @Action(LoadInvoices, { cancelUncompleted: true })
  @ImmutableContext()
  loadInvoices(
    { getState, setState }: StateContext<InvoicesStateModel>,
    { preserveResults }: LoadInvoices,
  ) {
    const nextPage = preserveResults ? getState().page + 1 : 1;

    return this.invoicesService.getInvoices(nextPage).pipe(
      tap((paginatedResults: PaginatedResults<Invoice>) => {
        return setState((state: InvoicesStateModel) => {
          state.page = nextPage;
          state.invoices = preserveResults
            ? [...(state.invoices || []), ...paginatedResults.results]
            : paginatedResults.results;
          state.totalCount = paginatedResults.count;

          return state;
        });
      }),
    );
  }

  @Action(DownloadInvoice, { cancelUncompleted: true })
  @ImmutableContext()
  downloadInvoice(
    {}: StateContext<InvoicesStateModel>,
    { invoiceId }: DownloadInvoice,
  ) {
    return this.invoicesService.getInvoiceFile(invoiceId).pipe(
      tap((invoiceFile: InvoiceFile) => {
        const byteCharacters = atob(invoiceFile.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = invoiceFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }),
    );
  }
}
